import { CanvaSX } from '@lib/canvasx/canvasx';
import { isFillStruct } from '@lib/canvasx/shapes';

// Path comes from vector path from design
const PATH =
  'M6.08889 7.89069C5.28889 8.69069 4.88889 9.09069 4.39184 9.09069C3.89478 9.09069 3.49478 8.69069 2.69478 7.89069L0.790688 5.98659C0.354003 5.54991 0.354004 4.8419 0.790689 4.40522V4.40522C1.22827 3.96764 1.93805 3.96866 2.37436 4.40751L2.98775 5.02448C3.50672 5.54646 3.76621 5.80745 4.07062 5.89272C4.28119 5.95171 4.50388 5.95203 4.71463 5.89366C5.01929 5.80927 5.27953 5.54903 5.80001 5.02855L9.63264 1.19591C10.0661 0.762443 10.7683 0.760624 11.2041 1.19184V1.19184C11.643 1.62624 11.6448 2.33477 11.2082 2.77143L6.08889 7.89069Z';

export const CHECKBOX_SIZE = 24;
const LINE_WIDTH = 2;

const CHECKBOX_WIDTH = 13;
const CHECKBOX_HEIGHT = 10;
const DASH_WIDTH = 16;
const DASH_HEIGHT = 4;

type DefaultRectProps = Omit<ShapeStruct, 'width' | 'height'>;
export type CheckboxProps = {
  type: 'checkbox' | 'dash' | 'empty';
} & Partial<ShapeStruct>;
export const Checkbox: CanvaSXComponent<CheckboxProps, DefaultRectProps> =
  function (props) {
    const { fill = 'black', type = 'checkbox' } = props;
    const { ctx } = CanvaSX.context;

    const renderFunc = ctx.roundRect ? 'roundRect' : 'rect';

    // When stroking vs filling, we have to adjust for the line width
    const offset = type === 'empty' ? LINE_WIDTH / 2 : 0;
    const size = type === 'empty' ? CHECKBOX_SIZE - LINE_WIDTH : CHECKBOX_SIZE;

    // dimensions of the checkmark or dash
    const width = type === 'checkbox' ? CHECKBOX_WIDTH : DASH_WIDTH;
    const height = type === 'checkbox' ? CHECKBOX_HEIGHT : DASH_HEIGHT;
    const x = (CHECKBOX_SIZE - width) / 2;
    const y = (CHECKBOX_SIZE - height) / 2;

    const borderRadius = 4;
    const fillColor = isFillStruct(fill) ? fill.color : fill;
    const opacity = isFillStruct(fill) ? fill.opacity ?? 1 : 1;

    ctx.save();
    ctx.fillStyle = fillColor;
    ctx.strokeStyle = fillColor;
    ctx.lineWidth = LINE_WIDTH;
    ctx.globalAlpha = opacity;

    if (type === 'empty') {
      ctx.beginPath();
      // Stroke empty box
      // @ts-expect-error this will always be a valid function
      ctx[renderFunc](offset, offset, size, size, borderRadius);
      ctx.closePath();
      ctx.fillStyle = 'white';
      ctx.fill();
      ctx.stroke();
    } else {
      // Fill background of box.
      ctx.beginPath();
      // @ts-expect-error this will always be a valid function
      ctx[renderFunc](offset, offset, size, size, borderRadius);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // Draw the icon in front.
      ctx.fillStyle = 'white';
      ctx.strokeStyle = 'white';
      if (type === 'checkbox') {
        let path;
        // @ts-expect-error createPath will exist when rendering to SVG or testing.
        if (ctx.createPath) {
          // @ts-expect-error createPath will exist when rendering to SVG or testing.
          path = ctx.createPath(PATH);
        } else {
          path = new Path2D(PATH);
        }
        ctx.translate(x, y);
        ctx.fill(path);
      } else {
        ctx.beginPath();
        // @ts-expect-error this will always be a valid function
        ctx[renderFunc](x, y, width, height, 8);
        ctx.closePath();
        ctx.fill();
      }
    }

    ctx.restore();

    return null;
  };

Checkbox.calculateDimensions = () => {
  return { width: CHECKBOX_SIZE, height: CHECKBOX_SIZE };
};
